import { Component, ElementRef, Inject, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import type { EstimateRenderData } from '@encounter';
import { NarrativeEstimateRenderConfig } from '../../../models/narrative-estimate-render-config';

export const NARRATIVE_PRINTING_CLASS = 'narrative-estimate-printing';
export const NARRATIVE_PRINT_CONTAINER_CLASS = 'narrative-estimate-print-container';
export const NARRATIVE_PRINT_LABEL = 'Print Estimate';

@Component({
    selector: 'estimate-narrative-renderer',
    templateUrl: './narrative-estimate-renderer.component.html',
    styleUrls: ['../../../scss/styles.scss'],
    styles: [
        `
                     :host {
                         display: contents;
                     }

                     /*
                      * <estimate> is hosted on several pages, so printing clones the estimate into a
                      * body-level container and hides every other body child. The rules are global
                      * (::ng-deep) but inert unless print() has set the class on <html>.
                      */
                     ::ng-deep .narrative-estimate-print-container {
                         display: none;
                     }

                     @media print {
                         ::ng-deep html.narrative-estimate-printing body > *:not(.narrative-estimate-print-container) {
                             display: none !important;
                         }

                         ::ng-deep html.narrative-estimate-printing .narrative-estimate-print-container {
                             display: block;
                         }
                     }
                 `,
    ],
    standalone: false
})
export class NarrativeEstimateRendererComponent implements OnChanges, OnDestroy {
    @Input() public estimate: EstimateRenderData | undefined;

    @ViewChild('printRoot', { static: false })
    private printRoot: ElementRef<HTMLElement> | undefined;

    public config: NarrativeEstimateRenderConfig | undefined;
    public readonly printLabel = NARRATIVE_PRINT_LABEL;

    private printContainer: HTMLElement | undefined;

    constructor(@Inject(DOCUMENT) private readonly document: Document) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['estimate']) {
            // GraphQL hosts nest the render config under estimateConfig; shopping passes the REST shape directly.
            this.config = this.estimate?.estimateConfig?.estimate || this.estimate;
        }
    }

    public print(): void {
        const root = this.printRoot?.nativeElement;
        const view = this.document.defaultView;

        if (!root || !view) {
            return;
        }

        this.cleanupPrint();

        const container = this.document.createElement('div');
        container.className = NARRATIVE_PRINT_CONTAINER_CLASS;
        container.appendChild(root.cloneNode(true));

        this.document.body.appendChild(container);
        this.document.documentElement.classList.add(NARRATIVE_PRINTING_CLASS);
        this.printContainer = container;

        view.addEventListener('afterprint', this.cleanupPrint, { once: true });
        view.print();
    }

    ngOnDestroy(): void {
        this.document.defaultView?.removeEventListener('afterprint', this.cleanupPrint);
        this.cleanupPrint();
    }

    private readonly cleanupPrint = (): void => {
        this.printContainer?.remove();
        this.printContainer = undefined;
        this.document.documentElement.classList.remove(NARRATIVE_PRINTING_CLASS);
    };
}
